"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import styles from "./FoodCategoryShowcase.module.scss";

const AUTOPLAY_DELAY = 4000;
const SWIPE_THRESHOLD = 50;
const MOBILE_BREAKPOINT = 768;

export default function FoodCategoryShowcase({ items = [], height = 400, mobileHeight = null, url = null, bottom = 28 }) {
  const [isMobile, setIsMobile] = useState(false);
  const [current, setCurrent] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [animated, setAnimated] = useState(false);

  const startX = useRef(0);
  const autoplayRef = useRef(null);
  const pauseRef = useRef(false);
  const trackRef = useRef(null);
  const showcaseRef = useRef(null);

  const total = items.length;

  /* ---- Responsive check ---- */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ---- Scroll animation ---- */
  useEffect(() => {
    if (isMobile || animated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimated(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile, animated]);

  /* ---- Autoplay ---- */
  const startAutoplay = useCallback(() => {
    stopAutoplay();
    if (!isMobile || total <= 1) return;
    autoplayRef.current = setInterval(() => {
      if (!pauseRef.current) {
        setCurrent((prev) => (prev + 1) % total);
      }
    }, AUTOPLAY_DELAY);
  }, [isMobile, total]);

  const stopAutoplay = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  };

  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [startAutoplay]);

  /* ---- Pause / resume helpers ---- */
  const pauseInteraction = () => {
    pauseRef.current = true;
  };

  const resumeInteraction = () => {
    pauseRef.current = false;
  };

  /* ---- Touch handlers ---- */
  const onTouchStart = (e) => {
    pauseInteraction();
    setDragging(true);
    startX.current = e.touches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (!dragging) return;
    const diff = e.touches[0].clientX - startX.current;
    setDragOffset(diff);
  };

  const onTouchEnd = () => {
    setDragging(false);
    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset < 0) {
        setCurrent((prev) => (prev + 1) % total);
      } else {
        setCurrent((prev) => (prev - 1 + total) % total);
      }
    }
    setDragOffset(0);
    resumeInteraction();
  };

  /* ---- Pointer (mouse) handlers for desktop testing ---- */
  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return; // handled by touch events
    pauseInteraction();
    setDragging(true);
    startX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging || e.pointerType === "touch") return;
    const diff = e.clientX - startX.current;
    setDragOffset(diff);
  };

  const onPointerUp = (e) => {
    if (e.pointerType === "touch") return;
    setDragging(false);
    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset < 0) {
        setCurrent((prev) => (prev + 1) % total);
      } else {
        setCurrent((prev) => (prev - 1 + total) % total);
      }
    }
    setDragOffset(0);
    resumeInteraction();
  };

  /* ---- Indicator click ---- */
  const goTo = (index) => {
    pauseInteraction();
    setCurrent(index);
    setTimeout(resumeInteraction, 600);
  };

  /* ---- Shared card content ---- */
  const renderCard = (item, mobile = false) => {
    const imgSrc = mobile && item.mobileImage ? item.mobileImage : item.image;
    const itemUrl = item.url || url;
    const cardContent = (
      <>
        <Image
          src={imgSrc}
          alt={item.alt || item.text}
          width={470}
          height={770}
          sizes={mobile ? "100vw" : "25vw"}
          className={styles.image}
          draggable={false}
        />
        <div className={styles.label} style={{ bottom: `${bottom}px` }}>
          <span className={styles.accentLine} />
          <p className={styles.labelText}>{item.text}</p>
        </div>
      </>
    );

    if (itemUrl) {
      return <Link href={itemUrl} className={styles.cardLink}>{cardContent}</Link>;
    }
    return cardContent;
  };

  /* ============ DESKTOP ============ */
  if (!isMobile) {
    return (
      <div ref={showcaseRef} className={styles.showcase} style={{ height: `${height}px` }}>
        <div className={styles.grid}>
          {items.map((item, i) => (
            <div
              key={i}
              className={`${styles.gridCard}${animated ? ` ${styles.animate}` : ""}`}
              style={{ animationDelay: `${i * 0.2}s` }}
            >
              {renderCard(item, false)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ============ MOBILE ============ */
  const translateX = -(current * 100) + (dragOffset / (trackRef.current?.offsetWidth || 1)) * 100;
  const currentHeight = mobileHeight || height;

  return (
    <div className={styles.showcase} style={{ height: `${currentHeight}px` }}>
      <div
        className={styles.carousel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Indicators */}
        <div className={styles.indicators}>
          {items.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot}${i === current ? ` ${styles.active}` : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
{/* del test */}
        {/* Track */}
        <div
          ref={trackRef}
          className={`${styles.track}${dragging ? ` ${styles.dragging}` : ""}`}
          style={{ transform: `translateX(${translateX}%)` }}
        >
          {items.map((item, i) => {
            const imgSrc = item.mobileImage || item.image;
            const itemUrl = item.url || url;
            const slideContent = (
              <>
                <img
                  src={imgSrc}
                  alt={item.alt || item.text}
                  width={430}
                  height={300}
                  className={styles.image}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  draggable={false}
                />
                <div className={styles.label}>
                  <span className={styles.accentLine} />
                  <p className={styles.labelText}>{item.text}</p>
                </div>
              </>
            );
            return (
              <div key={i} className={styles.slide}>
                {itemUrl ? <Link href={itemUrl} className={styles.cardLink}>{slideContent}</Link> : slideContent}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
